"use client";

import { TIndustriesConfig } from "@/app/config";
import { motion } from "motion/react";
import React from "react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      duration: 0.6
    }
  }
};

export default function GlobalEntertainmentMarkets({config}:{config: TIndustriesConfig['globalEntertainmentMarkets']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-white"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
           {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
           {config.description}
          </motion.p>
        </motion.div>

        {/* Market Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={containerVariants}
        >
          {config.marketData.map((market, index) => (
            <motion.div
              key={index}
              className={`${market.bgColor} ${market.borderColor} border rounded-xl p-6 relative cursor-pointer overflow-hidden`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 60,
                  scale: 0.9
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={ {
                y: -12,
                scale: 1.03,
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              whileTap={{ scale: 0.98 }}
              style={{ 
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
              }}
            >
              {/* Growth Rate Badge */}
              <motion.div 
                className="absolute top-4 right-4"
                variants={{
                  hidden: { opacity: 0, scale: 0.5, rotate: -10 },
                  visible: {
                    opacity: 1,
                    scale: 1,
                    rotate: 0,
                    transition: {
                      duration: 0.6,
                      ease: "backOut"
                    }
                  }
                }}
                whileHover={{ 
                  scale: 1.1,
                  rotate: 5,
                  transition: { duration: 0.2 }
                }}
              >
                <span className={`${market.badgeColor} text-white text-sm font-semibold px-3 py-1 rounded-full shadow-lg`}>
                  {market.growthRate}
                </span>
              </motion.div>

              {/* Region Header */}
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.2, duration: 0.6 }}
              >
                <motion.h3 
                  className={`text-xl font-semibold ${market.regionColor} mb-1`}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  {market.region}
                </motion.h3>
                <motion.p 
                  className="text-sm text-gray-600"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                >
                  {market.subtitle}
                </motion.p>
              </motion.div>

              {/* Primary Sectors */}
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.4, duration: 0.6 }}
              >
                <motion.p 
                  className="text-sm text-gray-700 font-medium mb-2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.5, duration: 0.4 }}
                >
                  Primary Sectors
                </motion.p>
                <motion.div 
                  className="flex flex-wrap gap-2"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.05,
                        delayChildren: index * 0.1 + 0.6
                      }
                    }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {market.primarySectors.map((sector, sectorIndex) => (
                    <motion.span 
                      key={sectorIndex}
                      className="text-xs bg-white bg-opacity-70 text-gray-700 px-2 py-1 rounded-md hover:bg-opacity-90 transition-all duration-200"
                      variants={{
                        hidden: { opacity: 0, scale: 0.8, y: 10 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover={{ 
                        scale: 1.05,
                        y: -2,
                        transition: { duration: 0.2 }
                      }}
                    >
                      {sector}
                    </motion.span>
                  ))}
                </motion.div>
              </motion.div>

              {/* Market Metrics */}
              <motion.div 
                className="grid grid-cols-2 gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.7, duration: 0.6 }}
              >
                {/* Market Revenue */}
                <motion.div
                  variants={ {
                    hidden: { opacity: 0, x: -20 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.p 
                    className="text-sm text-gray-700 font-medium mb-1"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.8, duration: 0.4 }}
                  >
                    {market.revenueLabel}
                  </motion.p>
                  <motion.p 
                    className={`text-lg font-bold ${market.revenueColor}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: index * 0.1 + 0.9, 
                      duration: 0.5,
                      ease: "backOut"
                    }}
                  >
                    {market.marketRevenue}
                  </motion.p>
                </motion.div>

                {/* Growth Rate */}
                <motion.div
                  variants={ {
                    hidden: { opacity: 0, x: -20 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.p 
                    className="text-sm text-gray-700 font-medium mb-1"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.8, duration: 0.4 }}
                  >
                    {market.growthLabel}
                  </motion.p>
                  <motion.p 
                    className="text-lg font-bold text-green-600"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: index * 0.1 + 0.9, 
                      duration: 0.5,
                      ease: "backOut"
                    }}
                  >
                    {market.growthRate}
                  </motion.p>
                </motion.div>
              </motion.div>

              {/* Top Players */}
              <motion.div 
                className="mt-4 pt-4 border-t border-white border-opacity-50"
                variants={ {
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut"
                    }
                  }
                }  }
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 1.0 }}
              >
                <motion.p 
                  className="text-sm text-gray-700 font-medium mb-1"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 1.1, duration: 0.4 }}
                >
                  {market.playersLabel}
                </motion.p>
                <motion.p 
                  className="text-sm text-gray-600 leading-relaxed"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 1.2, duration: 0.5 }}
                >
                  {market.topPlayers}
                </motion.p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}